import { DashboardBarChart } from "@aidkitorg/types/lib/admin-panel/dashboard";
import React, { useEffect, useState } from "react";
import { generateDistroSummaryKind } from "../../../utils/adminPanel.utils";
import { ReportFilterConfig } from "@aidkitorg/types/lib/admin-panel/filters";
import { BooleanExpr } from "@aidkitorg/types/lib/survey";
import { FilterGroupToBooleanExprConverter, BooleanExprToFilterGroupConverter } from "../../shared/utils/filterConverters";
import { useSettingsFilterFields } from "../../Filters/hooks/useSettingsFilterFields";

export type XAxis = {
  title: string,
  primaryField: string,
  secondaryField?: string
}

export type YAxis = {
  title: string,
  aggregation: string,
  dataField: string,
}

export const useBarChartFormState = (component?: DashboardBarChart) => {
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [filterConfig, setFilterConfig] = useState<ReportFilterConfig | undefined>(undefined)
  const [order, setOrder] = useState<'ascending' | 'descending' | undefined>(undefined)
  const availableFieldsForFilters = useSettingsFilterFields()

  const [xAxis, setXAxis] = useState<XAxis>({
    title: '',
    primaryField: ''
  })
  const [yAxis, setYAxis] = useState<YAxis>({
    title: '',
    aggregation: 'Count',
    dataField: '',
  })
  
  useEffect(() => {
    if (!component) return

    const componentTitle = 'title' in component && component.title
      ? typeof component.title === 'string'
        ? component.title
        : component.title['en']
      : 'Dashboard Component';
    setTitle(componentTitle)
    setDescription(component.description || '')

    // Load filter config if present
    if (component.filter) {
      try {
        const filterGroup = BooleanExprToFilterGroupConverter.convert(component.filter, availableFieldsForFilters);
        if (filterGroup) {
          setFilterConfig({
            enabled: true,
            config: filterGroup
          });
        }
      } catch (error) {
        console.warn('Failed to convert bar chart filter to FilterConfig:', error);
      }
    }

    const groups = component.groups
    if (groups.length === 1 && groups[0].kind === 'Field') {
      setXAxis({
        title: component.xAxisLabel || '',
        primaryField: groups[0].field,
      })
    }

    const bars = component.bars
    if (bars && bars.kind === 'Grouped') {
      const barsValue = bars.values
      if (barsValue.length === 1 && barsValue[0].kind === 'Field') {
        setXAxis((prev) => ({ ...prev, secondaryField: (barsValue[0] as { kind: 'Field'; field: string }).field }))
      }
    }

    const yAxisSummary: YAxis = {
      title: component.yAxisLabel || '',
      aggregation: component.summary.kind,
      dataField: ''
    }

    if (component.summary.kind === 'Sum' || component.summary.kind === 'Mean') {
      if (component.summary.value.kind === 'Field') {
        yAxisSummary.dataField = component.summary.value.field
      }
    }
    setYAxis(yAxisSummary)

    // Load order if present
    if (component.order) {
      setOrder(component.order)
    }
  }, [component])

  const buildBarChartObject = (): DashboardBarChart => {
    // Convert FilterConfig to BooleanExpr if filter is enabled
    let booleanExpr: BooleanExpr | undefined = undefined;
    if (filterConfig && filterConfig.enabled && filterConfig.config) {
      try {
        booleanExpr = FilterGroupToBooleanExprConverter.convert(filterConfig.config);
      } catch (error) {
        console.warn('Failed to convert filter config to boolean expression:', error);
      }
    }

    const barChart: DashboardBarChart = {
      kind: 'Bar Chart',
      title: title,
      description: description,
      summary: generateDistroSummaryKind(yAxis.aggregation, yAxis.dataField),
      groups: [{
        kind: 'Field',
        field: xAxis.primaryField
      }],
      xAxisLabel: xAxis.title,
      yAxisLabel: yAxis.title,
      ...(booleanExpr && { filter: booleanExpr }),
      ...(xAxis.secondaryField && {
        bars: {
          kind: 'Grouped',
          values: [{
            kind: 'Field',
            field: xAxis.secondaryField
          }]
        }
      }),
      ...(order && { order })
    }

    return barChart
  }
  const isFormStateValid =
    title !== '' &&
    xAxis.title !== '' &&
    xAxis.primaryField !== '' &&
    yAxis.title !== '' &&
    (yAxis.aggregation === 'Count' || yAxis.dataField !== '')

  return {
    title, setTitle,
    description, setDescription,
    xAxis, setXAxis,
    yAxis, setYAxis,
    filterConfig, setFilterConfig,
    order, setOrder,
    buildBarChartObject,
    isFormStateValid,
  }
}